import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { deleteUser } from "../../../redux/userRelated/userHandle";
import { getAllSclasses, getSubjectList, updateSclass } from "../../../redux/sclassRelated/sclassHandle";
import {
    getAllTeachers,
    removeTeachSubject,
    updateTeacher,
    updateTeachSubject,
} from "../../../redux/teacherRelated/teacherHandle";
import HomeroomAssignSubjectsFields from "../../../components/admin/HomeroomAssignSubjectsFields";
import {
    getTeacherSubjectIdsInClass,
    HOMEROOM_ASSIGN_SUBJECTS_MSG,
    syncTeacherSubjectsForClass,
} from "../../../utils/homeroomAssignSubjects";
import {
    Alert,
    alpha,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import AddCardIcon from "@mui/icons-material/AddCard";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import { CLASS_TEACHER_REQUIRES_SUBJECT_MSG } from "../../../utils/classTeacherUtils";
import { getStaffRoleLabel, isHomeroomTeacherRole, normalizeStaffRole } from "../../../utils/staffRoles";

const BASE_HEAD_CELLS = [
    { id: "name", label: "Class name", width: "22%" },
    { id: "teacher", label: "Homeroom teacher", width: "22%" },
    { id: "occupancy", label: "Students", width: "16%" },
    { id: "room", label: "Room / venue", width: "18%" },
    { id: "actions", label: "Actions", align: "right", width: "22%" },
];

function TableSkeleton({ columns = BASE_HEAD_CELLS }) {
    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell key={c.id} sx={{ fontWeight: 600 }}>
                                {c.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <TableRow key={i}>
                            <TableCell colSpan={5}>
                                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function RowActions({ row, onDeleteClass, homeroomLabel, onAssignHomeroom }) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);

    return (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
            <Tooltip title="Class details" arrow>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => navigate(`/Admin/classes/class/${row.id}`)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
                    }}
                >
                    <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Add subjects" arrow>
                <IconButton
                    size="small"
                    onClick={() =>
                        navigate(`/Admin/addsubject/${row.id}`, {
                            state: { className: row.name },
                        })
                    }
                    sx={{
                        bgcolor: (t) => alpha(t.palette.secondary.main, 0.1),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.secondary.main, 0.18) },
                    }}
                >
                    <PostAddIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Add students" arrow>
                <IconButton
                    size="small"
                    onClick={() => navigate(`/Admin/class/addstudents/${row.id}`)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.success.main, 0.16) },
                    }}
                >
                    <PersonAddAlt1Icon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Bulk upload CSV" arrow>
                <IconButton
                    size="small"
                    onClick={() => navigate(`/Admin/class/${row.id}/bulk-upload`)}
                    sx={{
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                        "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.16) },
                    }}
                >
                    <UploadFileOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="More" arrow>
                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onAssignHomeroom(row);
                    }}
                >
                    Assign {homeroomLabel}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setMenuAnchor(null);
                        onDeleteClass(row);
                    }}
                    sx={{ color: "error.main", gap: 1 }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                    Delete class
                </MenuItem>
            </Menu>
        </Stack>
    );
}

const ShowClasses = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { sclassesList, subjectsList, subloading, loading, error, getresponse } = useSelector((state) => state.sclass);
    const { teachersList } = useSelector((state) => state.teacher);
    const { currentUser } = useSelector((state) => state.user);

    const adminID = currentUser._id;
    const homeroomTerm = useHomeroomTeacherTerm();
    const headCells = useMemo(
        () =>
            BASE_HEAD_CELLS.map((c) =>
                c.id === "teacher" ? { ...c, label: homeroomTerm.assignOnClass } : c,
            ),
        [homeroomTerm.assignOnClass],
    );

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [homeroomDialog, setHomeroomDialog] = useState(null);
    const [homeroomSaving, setHomeroomSaving] = useState(false);

    useEffect(() => {
        dispatch(getAllSclasses(adminID, "Sclass"));
        dispatch(getAllTeachers(adminID));
    }, [adminID, dispatch]);

    const rows = useMemo(() => {
        if (!Array.isArray(sclassesList) || sclassesList.length === 0) return [];
        return sclassesList.map((sclass) => {
            const enrolled = sclass._count?.students ?? 0;
            const cap = sclass.studentCapacity;
            const occupancyLabel =
                cap != null && cap > 0 ? `${enrolled} / ${cap}` : `${enrolled} enrolled`;
            const atCap = cap != null && cap > 0 && enrolled >= cap;
            return {
                name: sclass.sclassName,
                id: sclass._id,
                teacherName: sclass.classTeacher?.name ?? "—",
                classTeacherId: sclass.classTeacher?.id ?? sclass.classTeacher?._id ?? "",
                occupancyLabel,
                atCap,
                room: sclass.room?.trim() || "—",
            };
        });
    }, [sclassesList]);

    const schoolType = currentUser?.schoolType;
    const homeroomTeacherOptions = useMemo(() => {
        if (!Array.isArray(teachersList)) return [];
        return teachersList
            .map((t) => ({
                id: t._id || t.id,
                name: t.name || "—",
                role: normalizeStaffRole(t.role),
                roleLabel: getStaffRoleLabel(t.role, schoolType),
            }))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    }, [teachersList, schoolType]);

    const classSubjects = useMemo(
        () => (Array.isArray(subjectsList) ? subjectsList : []),
        [subjectsList],
    );

    const [deleteDialog, setDeleteDialog] = useState(null);

    const refreshList = () => {
        dispatch(getAllSclasses(adminID, "Sclass"));
        dispatch(getAllTeachers(adminID));
    };

    const openHomeroomAssignDialog = (classRow) => {
        const preTeacher = classRow.classTeacherId || "";
        const teacher = teachersList?.find((t) => (t._id || t.id) === preTeacher);
        dispatch(getSubjectList(classRow.id, "ClassSubjects"));
        setHomeroomDialog({
            classRow,
            teacherId: preTeacher,
            selectedSubjectIds: teacher ? getTeacherSubjectIdsInClass(teacher, classRow.id) : [],
            setRoleToo: teacher ? !isHomeroomTeacherRole(teacher.role) : true,
        });
    };

    const handleHomeroomTeacherChange = (teacherId) => {
        const teacher = teachersList?.find((t) => (t._id || t.id) === teacherId);
        const classId = homeroomDialog?.classRow?.id;
        setHomeroomDialog((prev) =>
            prev
                ? {
                      ...prev,
                      teacherId,
                      selectedSubjectIds:
                          teacher && classId ? getTeacherSubjectIdsInClass(teacher, classId) : [],
                      setRoleToo: teacher ? !isHomeroomTeacherRole(teacher.role) : true,
                  }
                : prev,
        );
    };

    const toggleHomeroomSubject = (subjectId) => {
        setHomeroomDialog((prev) => {
            if (!prev) return prev;
            const set = new Set(prev.selectedSubjectIds || []);
            if (set.has(subjectId)) set.delete(subjectId);
            else set.add(subjectId);
            return { ...prev, selectedSubjectIds: [...set] };
        });
    };

    const closeHomeroomDialog = () => setHomeroomDialog(null);

    const saveHomeroomAssign = async () => {
        if (!homeroomDialog?.classRow?.id || !homeroomDialog?.teacherId) return;
        if (!homeroomDialog.selectedSubjectIds?.length) {
            setMessage(HOMEROOM_ASSIGN_SUBJECTS_MSG);
            setShowPopup(true);
            return;
        }
        setHomeroomSaving(true);
        const { classRow, teacherId, setRoleToo } = homeroomDialog;
        const teacher = teachersList?.find((t) => (t._id || t.id) === teacherId);
        const teacherName = teacher?.name || "Teacher";

        const syncOutcome = await syncTeacherSubjectsForClass(dispatch, {
            teacherId,
            classId: classRow.id,
            selectedSubjectIds: homeroomDialog.selectedSubjectIds,
            teacher,
            updateTeachSubject,
            removeTeachSubject,
        });
        if (!syncOutcome.success) {
            setHomeroomSaving(false);
            setMessage(syncOutcome.message || CLASS_TEACHER_REQUIRES_SUBJECT_MSG);
            setShowPopup(true);
            return;
        }

        const classOutcome = await dispatch(updateSclass(classRow.id, { classTeacherId: teacherId }));
        if (classOutcome?.success === false) {
            setHomeroomSaving(false);
            setMessage(classOutcome?.message || CLASS_TEACHER_REQUIRES_SUBJECT_MSG);
            setShowPopup(true);
            return;
        }
        if (setRoleToo) {
            await dispatch(updateTeacher(teacherId, { role: homeroomTerm.roleValue }));
        }
        setHomeroomSaving(false);
        setHomeroomDialog(null);
        refreshList();
        setMessage(
            `${teacherName} is now ${homeroomTerm.assignOnClass} of ${classRow.name} (${syncOutcome.subjectCount} subject(s) assigned)${
                setRoleToo ? ` · ${homeroomTerm.roleTitleCaps} role set` : ""
            }.`,
        );
        setShowPopup(true);
    };

    const closeDeleteDialog = () => setDeleteDialog(null);

    const openDeleteClassDialog = (row) => {
        setDeleteDialog({
            title: `Delete class: ${row.name}`,
            description:
                "This will permanently delete this class and related subjects, student links, and fee records tied to it.",
            confirmWord: row.name,
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(row.id, "Sclass"));
                if (outcome?.success) {
                    refreshList();
                    return;
                }
                setMessage("Could not delete the class. Check the console or try again.");
                setShowPopup(true);
            },
        });
    };

    const openDeleteAllClassesDialog = () => {
        setDeleteDialog({
            title: "Delete all classes",
            description: "This will permanently delete every class for this school. This cannot be undone.",
            confirmWord: "DELETE ALL CLASSES",
            onConfirm: async () => {
                const outcome = await dispatch(deleteUser(adminID, "Sclasses"));
                if (outcome?.success) {
                    refreshList();
                    return;
                }
                setMessage("Could not delete all classes. Ensure no blocking records exist, then try again.");
                setShowPopup(true);
            },
        });
    };

    const handleConfirmDelete = async () => {
        if (deleteDialog?.onConfirm) {
            await deleteDialog.onConfirm();
        }
        closeDeleteDialog();
    };

    const hasRows = rows.length > 0;
    const showEmpty = !loading && !error && !hasRows;
    const showTable = !loading && !error && hasRows;
    const paginated = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box
            component="main"
            sx={{
                minHeight: "100%",
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 28%)`
                        : alpha(t.palette.common.black, 0.2),
                pb: 6,
            }}
        >
            <Container maxWidth="lg" sx={{ pt: { xs: 2, sm: 3 }, pb: 2 }}>
                <Fade in timeout={380}>
                    <Stack spacing={3}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2.5, sm: 3 },
                                borderRadius: 3,
                                color: "common.white",
                                background: (t) =>
                                    `linear-gradient(120deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 55%, ${alpha(
                                        t.palette.primary.light,
                                        0.95
                                    )} 100%)`,
                                boxShadow: (t) => `0 12px 40px ${alpha(t.palette.primary.main, 0.28)}`,
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: alpha("#fff", 0.15),
                                        }}
                                    >
                                        <ClassOutlinedIcon sx={{ fontSize: 32 }} />
                                    </Box>
                                    <Stack spacing={0.75}>
                                        <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                            Curriculum
                                        </Typography>
                                        <Typography variant="h4" component="h1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                                            Classes
                                        </Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9, maxWidth: 520 }}>
                                            Create cohorts, open a class hub for subjects and students, and keep the list
                                            tidy on one surface.
                                        </Typography>
                                    </Stack>
                                </Stack>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<AddCardIcon />}
                                        onClick={() => navigate("/Admin/addclass")}
                                        sx={{
                                            bgcolor: "common.white",
                                            color: "primary.main",
                                            fontWeight: 600,
                                            px: 2.5,
                                            py: 1.25,
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: (t) => alpha(t.palette.common.white, 0.92),
                                                boxShadow: (t) => `0 4px 20px ${alpha(t.palette.common.black, 0.12)}`,
                                            },
                                        }}
                                    >
                                        Add class
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        color="inherit"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={openDeleteAllClassesDialog}
                                        sx={{
                                            borderColor: alpha("#fff", 0.55),
                                            color: "common.white",
                                            borderRadius: 2,
                                            "&:hover": {
                                                borderColor: "common.white",
                                                bgcolor: alpha("#fff", 0.08),
                                            },
                                        }}
                                    >
                                        Delete all
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>

                        {error && (
                            <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                {error?.message || "Could not load classes."}
                            </Alert>
                        )}

                        {!error && (
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                                    boxShadow: (t) => `0 4px 24px ${alpha(t.palette.common.black, 0.06)}`,
                                }}
                            >
                                {loading && (
                                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                        <TableSkeleton columns={headCells} />
                                    </Box>
                                )}

                                {showEmpty && (
                                    <Box
                                        sx={{
                                            py: { xs: 6, sm: 8 },
                                            px: 2,
                                            textAlign: "center",
                                            transition: "opacity 0.35s ease",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 88,
                                                height: 88,
                                                mx: "auto",
                                                mb: 2,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                                color: "primary.main",
                                            }}
                                        >
                                            <SchoolOutlinedIcon sx={{ fontSize: 44 }} />
                                        </Box>
                                        <Typography variant="h6" gutterBottom fontWeight={600}>
                                            {getresponse ? "No classes yet" : "No classes loaded"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: "auto", mb: 3 }}>
                                            {typeof getresponse === "string"
                                                ? getresponse
                                                : "Add your first class to organise subjects and students."}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<AddCardIcon />}
                                            onClick={() => navigate("/Admin/addclass")}
                                            sx={{ borderRadius: 2, px: 3, fontWeight: 600 }}
                                        >
                                            Add your first class
                                        </Button>
                                    </Box>
                                )}

                                {showTable && (
                                    <>
                                        <Box
                                            sx={{
                                                px: { xs: 2, sm: 2.5 },
                                                py: 2,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 1,
                                                alignItems: "center",
                                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                All classes
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={`${rows.length} total`}
                                                color="primary"
                                                variant="outlined"
                                                sx={{ fontWeight: 600, borderRadius: 2 }}
                                            />
                                        </Box>
                                        <TableContainer>
                                            <Table size="medium" aria-label="classes table">
                                                <TableHead>
                                                    <TableRow>
                                                        {headCells.map((col) => (
                                                            <TableCell
                                                                key={col.id}
                                                                align={col.align || "left"}
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    fontSize: 12,
                                                                    letterSpacing: 0.6,
                                                                    textTransform: "uppercase",
                                                                    color: "text.secondary",
                                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                                                                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                                    py: 2,
                                                                    width: col.width,
                                                                }}
                                                            >
                                                                {col.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginated.map((row) => (
                                                        <TableRow
                                                            key={row.id}
                                                            hover
                                                            sx={{
                                                                transition: (t) =>
                                                                    t.transitions.create(["background-color", "box-shadow"], {
                                                                        duration: t.transitions.duration.shorter,
                                                                    }),
                                                                "&:hover": {
                                                                    boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}`,
                                                                },
                                                            }}
                                                        >
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Typography fontWeight={600}>{row.name}</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {row.teacherName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={row.occupancyLabel}
                                                                    color={row.atCap ? "warning" : "default"}
                                                                    variant={row.atCap ? "filled" : "outlined"}
                                                                    sx={{ fontWeight: 600, borderRadius: 2 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ py: 2 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {row.room}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ py: 2 }}>
                                                                <RowActions
                                                                    row={row}
                                                                    onDeleteClass={openDeleteClassDialog}
                                                                    homeroomLabel={homeroomTerm.assignOnClass}
                                                                    onAssignHomeroom={openHomeroomAssignDialog}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            component="div"
                                            rowsPerPageOptions={[5, 10, 25, 50]}
                                            count={rows.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={(_, p) => setPage(p)}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            sx={{
                                                borderTop: (t) => `1px solid ${t.palette.divider}`,
                                                "& .MuiTablePagination-toolbar": { px: 2 },
                                            }}
                                        />
                                    </>
                                )}
                            </Paper>
                        )}
                    </Stack>
                </Fade>
            </Container>
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
            <ConfirmDeleteDialog
                open={Boolean(deleteDialog)}
                onClose={closeDeleteDialog}
                onConfirm={handleConfirmDelete}
                title={deleteDialog?.title ?? ""}
                description={deleteDialog?.description ?? ""}
                confirmWord={deleteDialog?.confirmWord ?? ""}
            />
            <Dialog open={Boolean(homeroomDialog)} onClose={closeHomeroomDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {homeroomTerm.assignOnClass}: {homeroomDialog?.classRow?.name || ""}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Choose the teacher and which subjects they will teach in this class.
                    </Typography>
                    <Stack spacing={2}>
                        <FormControl fullWidth required>
                            <InputLabel>Teacher</InputLabel>
                            <Select
                                label="Teacher"
                                value={homeroomDialog?.teacherId || ""}
                                onChange={(e) => handleHomeroomTeacherChange(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>Select teacher</em>
                                </MenuItem>
                                {homeroomTeacherOptions.length === 0 ? (
                                    <MenuItem disabled value="">
                                        <em>No teachers on staff yet</em>
                                    </MenuItem>
                                ) : (
                                    homeroomTeacherOptions.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>
                                            {t.name}
                                            {t.roleLabel ? ` · ${t.roleLabel}` : ""}
                                        </MenuItem>
                                    ))
                                )}
                            </Select>
                        </FormControl>
                        <HomeroomAssignSubjectsFields
                            homeroomLabel={
                                homeroomTeacherOptions.find((t) => t.id === homeroomDialog?.teacherId)?.name
                            }
                            classId={homeroomDialog?.classRow?.id}
                            showClassSelect={false}
                            subjects={classSubjects}
                            subjectsLoading={subloading}
                            selectedSubjectIds={homeroomDialog?.selectedSubjectIds || []}
                            onToggleSubject={toggleHomeroomSubject}
                            currentTeacherId={homeroomDialog?.teacherId || ""}
                        />
                        {homeroomDialog?.teacherId &&
                        !isHomeroomTeacherRole(
                            homeroomTeacherOptions.find((t) => t.id === homeroomDialog.teacherId)?.role,
                        ) ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={Boolean(homeroomDialog.setRoleToo)}
                                        onChange={(e) =>
                                            setHomeroomDialog((prev) =>
                                                prev ? { ...prev, setRoleToo: e.target.checked } : prev,
                                            )
                                        }
                                    />
                                }
                                label={`Also set role to ${homeroomTerm.roleTitleCaps}`}
                            />
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeHomeroomDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={saveHomeroomAssign}
                        disabled={
                            homeroomSaving ||
                            !homeroomDialog?.teacherId ||
                            !homeroomDialog?.selectedSubjectIds?.length
                        }
                    >
                        {homeroomSaving ? "Saving…" : "Assign"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ShowClasses;
